import { useState, useEffect } from 'react';
import get from 'lodash/get';
import config from '../../config';
import styles from './VoteButton.module.scss';

function VoteButton({ votes: initialVotes, documentId, voteBatch }) {
  const [votes, setVotes] = useState(initialVotes || 0);
  const [isVoted, setIsVoted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const activeVotingSessions = config.activeVotingSessions || [];

  const showVoting =
    activeVotingSessions.includes('general') ||
    (voteBatch && activeVotingSessions.includes(voteBatch));

  // localStorage utility functions
  const storageKey = 'ja_program_voted_products';

  const getVotedProducts = () => {
    try {
      const votedProducts = localStorage.getItem(storageKey);
      return votedProducts ? JSON.parse(votedProducts) : [];
    } catch (e) {
      return [];
    }
  };

  const saveVote = (productId, voted) => {
    try {
      let votedProducts = getVotedProducts();
      if (voted) {
        if (!votedProducts.includes(productId)) {
          votedProducts.push(productId);
        }
      } else {
        votedProducts = votedProducts.filter(id => id !== productId);
      }
      localStorage.setItem(storageKey, JSON.stringify(votedProducts));
    } catch (e) {
      console.error('Error saving vote to localStorage:', e);
    }
  };

  // Initialize voted status from localStorage
  useEffect(() => {
    const votedProducts = getVotedProducts();
    const productVoted = votedProducts.includes(documentId);

    // If vote count is 0 but marked as voted, remove from localStorage
    if (productVoted && votes === 0) {
      saveVote(documentId, false);
      setIsVoted(false);
    } else {
      setIsVoted(productVoted);
    }
  }, [documentId, votes]);

  const handleVote = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isLoading) return;

    const action = isVoted ? 'remove' : 'add';
    const previousVotes = votes;
    const previousVotedState = isVoted;

    // Optimistic update
    setIsVoted(!isVoted);
    setIsLoading(true);

    try {
      const method = action === 'add' ? 'POST' : 'DELETE';

      // Build URL based on context
      let url;
      const headers = {
        'Content-Type': 'application/json',
      };

      if (config.isWordPress && config.restUrl) {
        // Use WordPress REST API route when in WordPress context
        url = `${config.restUrl}/vote/${documentId}`;
        // Add nonce for WordPress authentication
        if (config.nonce) {
          headers['X-WP-Nonce'] = config.nonce;
        }
      } else {
        // Use backend API directly in standalone mode
        url = `${config.apiEndpoint}/voting/api::product.product:${documentId}/vote`;
      }

      const response = await fetch(url, {
        method: method,
        headers: headers,
      });

      const data = await response.json();

      if (!response.ok || data?.message) {
        if (get(data, 'error.message') === `Already voted for api::product.product:${documentId}`) {
          saveVote(documentId, true);
          setIsVoted(true);
          setVotes(previousVotes);
          alert('You have already voted for this product.');
          return;
        } else {
          throw new Error(data?.message || 'Failed to vote');
        }
      }

      // Update with actual vote count from server
      if (data.votes !== undefined && data.votes !== null) {
        setVotes(data.votes);
      }

      // Save to localStorage
      saveVote(documentId, action === 'add');
    } catch (error) {
      console.error('Error voting:', error);

      // Revert optimistic updates
      setIsVoted(previousVotedState);
      setVotes(previousVotes);
      saveVote(documentId, previousVotedState);

      // Show error message
      if (error.message) {
        alert(error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {showVoting && (
        <div className={styles.productVoting}>
          <button
            className={`${styles.voteButton} ${isVoted ? styles.voted : ''}`}
            onClick={handleVote}
            disabled={isLoading}
            data-product-id={documentId}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill={isVoted ? '#ff6b6b' : 'none'}
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={styles.voteHeartIcon}
            >
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
            <span className={styles.voteText}>Like</span>
          </button>
          <span className={styles.voteCount}>{votes}</span>
          <span
            className={styles.voteLoader}
            style={{ opacity: isLoading ? 1 : 0 }}
          ></span>
        </div>
      )}
    </>
  );
}

export default VoteButton;
